import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { apiGet, apiPatch } from "../../lib/api";
import {
  buildAdminAuthHeaders,
  isUnauthorizedAdminError,
  shortProductId,
  toProductDraft,
  toProductPayload
} from "./admin-utils";
import "../Shop.css";

const EMPTY_DRAFT = {
  name: "",
  slug: "",
  description: "",
  price_cents: "",
  weight_grams: "",
  stock_qty: "",
  image_url: "",
  active: true
};

const AdminProductEditPage = () => {
  const { adminToken, handleUnauthorized } = useOutletContext();
  const navigate = useNavigate();
  const { productId: productIdParam } = useParams();
  const productId = useMemo(() => decodeURIComponent(productIdParam || ""), [productIdParam]);
  const [draft, setDraft] = useState(() => ({ ...EMPTY_DRAFT }));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const loadProduct = useCallback(async () => {
    if (!adminToken || !productId) {
      setIsLoading(false);
      if (!productId) {
        setError("ID prodotto non valido.");
      }
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const payload = await apiGet("/api/admin/products", {
        headers: buildAdminAuthHeaders(adminToken)
      });

      const products = Array.isArray(payload?.products) ? payload.products : [];
      const product = products.find((item) => item.id === productId);
      if (!product) {
        setError("Prodotto non trovato.");
        return;
      }

      setDraft(toProductDraft(product));
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : "Errore caricamento prodotto";
      if (isUnauthorizedAdminError(loadError)) {
        handleUnauthorized();
        return;
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [adminToken, handleUnauthorized, productId]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const handleDraftChange = (field, value) => {
    setDraft((current) => ({
      ...current,
      [field]: value
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!productId || isSaving) {
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      await apiPatch(`/api/admin/products/${productId}`, toProductPayload(draft), {
        headers: buildAdminAuthHeaders(adminToken)
      });

      navigate("/admin/products", {
        state: {
          notice: "Prodotto aggiornato."
        }
      });
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : "Errore aggiornamento prodotto";
      if (isUnauthorizedAdminError(saveError)) {
        handleUnauthorized();
        return;
      }
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="admin-section-block admin-edit-page">
      <div className="admin-section-header">
        <div>
          <h3 className="admin-section-title">Modifica prodotto</h3>
          <p className="admin-row-meta">
            ID: <strong>{shortProductId(productId)}</strong>
          </p>
        </div>
        <Link className="shop-back-link" to="/admin/products">
          Torna ai prodotti
        </Link>
      </div>

      {error && <p className="shop-state shop-error">Errore: {error}</p>}
      {isLoading && <p className="shop-state">Caricamento prodotto...</p>}

      {!isLoading && !error && (
        <form className="admin-product-create-form" onSubmit={handleSave}>
          <div className="admin-product-create-grid">
            <input
              className="admin-form-input"
              type="text"
              placeholder="Nome"
              value={draft.name}
              onChange={(event) => handleDraftChange("name", event.target.value)}
              required
            />
            <input
              className="admin-form-input"
              type="text"
              placeholder="Slug"
              value={draft.slug}
              onChange={(event) => handleDraftChange("slug", event.target.value)}
              required
            />
            <input
              className="admin-form-input"
              type="number"
              min="1"
              step="1"
              placeholder="Prezzo centesimi"
              value={draft.price_cents}
              onChange={(event) => handleDraftChange("price_cents", event.target.value)}
              required
            />
            <input
              className="admin-form-input"
              type="number"
              min="1"
              step="1"
              placeholder="Peso grammi"
              value={draft.weight_grams}
              onChange={(event) => handleDraftChange("weight_grams", event.target.value)}
              required
            />
            <input
              className="admin-form-input"
              type="number"
              min="0"
              step="1"
              placeholder="Stock"
              value={draft.stock_qty}
              onChange={(event) => handleDraftChange("stock_qty", event.target.value)}
              required
            />
            <input
              className="admin-form-input"
              type="url"
              placeholder="Image URL (opzionale)"
              value={draft.image_url}
              onChange={(event) => handleDraftChange("image_url", event.target.value)}
            />
            <input
              className="admin-form-input admin-form-input-wide"
              type="text"
              placeholder="Descrizione (opzionale)"
              value={draft.description}
              onChange={(event) => handleDraftChange("description", event.target.value)}
            />
            <label className="admin-checkbox-label">
              <input
                type="checkbox"
                checked={Boolean(draft.active)}
                onChange={(event) => handleDraftChange("active", event.target.checked)}
              />
              Attivo
            </label>
          </div>

          <div className="admin-edit-actions">
            <button type="submit" className="shop-add-button admin-save-button" disabled={isSaving}>
              {isSaving ? "Salvataggio..." : "Salva modifiche"}
            </button>
            <button type="button" className="cart-clear-button" onClick={() => navigate("/admin/products")}>
              Annulla
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default AdminProductEditPage;
